import { useDispatch, useSelector } from "@redux";
import { Button, Card, Checkbox, InputNumber, Switch, Table, TableProps, Typography } from "antd";
import { IRestColumnProps } from "components/rest/Table/TableColumn";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DynamicObjectType, FilterFromUrlResponseType } from "types/commons";
import { getMultipleLocationById } from "@redux/locations/actions";
import { useRestContext } from "components/form/RestInputContext";
import FormItem from "components/form/FormItem";
import SearchProductModal from "../SearchProductModal";
import { map, sortBy } from "lodash";

interface RelatedLocationTable {
   isCreate?: boolean;
   isOrderProcess?: boolean;
}

export default function RelatedLocationTable({ isCreate, isOrderProcess }: RelatedLocationTable) {
   const { t } = useTranslation();
   const dispatch = useDispatch();
   const { form } = useRestContext();
   const relatedLocationModalRef = useRef<{ open: (value) => void }>();
   const locationsCurr = useSelector((state) => state.locations.currentData) as DynamicObjectType;
   const [loading, setLoading] = useState(false);
   const [itemRemoveCurr, setItemRemoveCurr] = useState();

   const [dataSource, setDataSource] = useState([]);

   const updateRelatedLocationById = (arrayList, id, fieldKey, newPriority) => {
      return map(arrayList, obj => {
         if (obj.locationRelatedId === id) {
             return { ...obj, [fieldKey]: newPriority };
         }
         return obj;
     });
   }

   const handleInputNumberChange = (key, newValue, id) => {   
      const relatedLocationNew = sortBy(updateRelatedLocationById(dataSource, id, key, newValue), 'priority');
      setDataSource(relatedLocationNew);
   };

   const handleAddRelatedLocation = async (selectedRows) => {
      setLoading(true);
      const extractMultipleLocationById = await dispatch(getMultipleLocationById({
         data: {
            filters: [
               {
                  condition: 'and',
                  key: 'id',
                  operator: 'is',
                  value: selectedRows,
               },
            ]
         }
      })).then(({ payload: { data } }: DynamicObjectType) => {
         return data;
      });

      const multipleLocationByIdNew = extractMultipleLocationById?.map((item) => ({
         locationRelatedId: item?.id,
         code: item?.code,
         name: item?.name,
         priority: 0,
         rationSyncStock: 100,
         minValue: 0,
         maxValue: -1,
         isMarketPlace: false,
         isWebsite: true,
      }));

      const combineLocationRelated = sortBy(dataSource.length > 0 ? [...dataSource, ...multipleLocationByIdNew] : multipleLocationByIdNew, 'priority');
      setDataSource(combineLocationRelated);
      setLoading(false);
      relatedLocationModalRef.current.open(false);
   }

   const handleRemoveRelatedLocation = async (id) => {
      setItemRemoveCurr(id);
      const filterArray = dataSource?.filter((item) => item?.locationRelatedId !== id);
      setDataSource(filterArray);
      setItemRemoveCurr(null);
   };

   const mapRelatedLocation = (dataSource) => {
      if (Array.isArray(dataSource) && dataSource?.length > 0) {
         return dataSource.map((item) => ({
            key: item?.locationRelatedId,
            id: item?.locationRelatedId,
            name: item?.name,
            code: item?.code,
            priority: item?.priority,
            rationSyncStock: item?.rationSyncStock,
            minValue: item?.minValue,
            maxValue: item?.maxValue,
            isMarketPlace: item?.isMarketPlace,
            isWebsite: item?.isWebsite,
         }))
      } else {
         return [];
      }
   }   

   const initialFilterModal: FilterFromUrlResponseType = {
      join: ['detail', 'locationRelated'],
      filters: (isCreate && dataSource?.length === 0) ? [
         {
            condition: 'and',
            key: 'isOrderProcess',
            operator: 'is',
            value: false,
         },
         {
            condition: 'and',
            key: 'isSyncStock',
            operator: 'is',
            value: true,
         },
      ] :  (dataSource?.length > 0) ? [
        {
          condition: 'and',
          key: 'isOrderProcess',
          operator: 'is',
          value: false,
         },
         {
            condition: 'and',
            key: 'isSyncStock',
            operator: 'is',
            value: true,
         },
         {
            condition: 'and',
            key: 'id',
            operator: 'notin',
            value: dataSource?.map((item) => item?.locationRelatedId)?.concat(locationsCurr?.id),
         },
      ] : [
         {
            condition: 'and',
            key: 'isOrderProcess',
            operator: 'is',
            value: false,
         },
         {
            condition: 'and',
            key: 'isSyncStock',
            operator: 'is',
            value: true,
         },
         {
            condition: 'and',
            key: 'id',
            operator: 'ne',
            value: locationsCurr?.id,
         },
      ],
   };

   const columnsModal: IRestColumnProps[] = [
      {
        name: 'name',
        title: 'LOCATION',
        render: (detail: string, record) => (
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ color: '#AB2328', fontWeight: 600 }}>
               {`${detail} | ${record?.code}`}
            </Typography.Paragraph>
        ),
        isDefault: true,
     },
     {
        name: 'isOrderProcess',
        title: 'ORDER PROCESSING LOCATION',
        render: (d, r, i, handleEditRow) => (
           <Switch
            disabled
            onChange={(newVal) => {
               handleEditRow({ id: r.id, isOrderProcess: newVal });
            }}
            checked={!!d}
         />
       ),
        isDefault: true,
      },
      {
         name: 'isActive',
         title: 'ACTIVE',
         render: (detail: string) => (
            <div>
               <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ color: detail ? '#66C11F' : '#AB2328' }}>
                  {detail ? 'Active' : 'Inactive'}
               </Typography.Paragraph>
            </div>
         ),
         isDefault: true,
      },
   ];
   
   const columns: TableProps<DynamicObjectType>['columns'] = [
       {
         key: 'name',
         dataIndex: 'name',
         title: 'Location Name',
         render: (detail: string, record) => (
            <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ color: '#AB2328' }}>
               {`${detail} | ${record?.code}`}
            </Typography.Paragraph>
         ),
         width: 200,
         fixed: 'left',
         align: 'center',
      },
      {
         key: 'priority',
         dataIndex: 'priority',
         title: 'Priority',
         render: (d: number, record) => {            
            return (
               <InputNumber
                  min={0}
                  defaultValue={d}
                  onChange={(value) => handleInputNumberChange('priority', value, record?.id)}
                  formatter={(value) => String(parseInt((String(value || 0)), 10))}
                  parser={(value) => parseInt(value, 10)}
               />
            )
         },
         width: 120,
         align: 'center',
      },
      {
         key: 'rationSyncStock',
         dataIndex: 'rationSyncStock',
         title: '% SYNC stock',
         render: (d: number, record) => {
            return (
               <InputNumber
                  min={0}
                  defaultValue={d}
                  onChange={(value) => handleInputNumberChange('rationSyncStock', value, record?.id)}
                  formatter={(value) => String(parseInt((String(value || 0)), 10))}
                  parser={(value) => parseInt(value, 10)}
               />
            )
         },
         width: 160,
         align: 'center',
      },
      {
         key: 'minValue',
         dataIndex: 'minValue',
         title: 'Min value',
         render: (d: number, record) => {
            return (
               <InputNumber
                  min={0}
                  defaultValue={d || 0}
                  onChange={(value) => handleInputNumberChange('minValue', value, record?.id)}
                  formatter={(value) => String(parseInt((String(value || 0)), 10))}
                  parser={(value) => parseInt(value, 10)}
               />
            )
         },
         width: 120,
         align: 'center',
      },
      {
         key: 'maxValue',
         dataIndex: 'maxValue',
         title: 'Max value',
         render: (d: number, record) => {
            return (
               <InputNumber
                  min={0}
                  defaultValue={d || 0}
                  onChange={(value) => handleInputNumberChange('maxValue', value, record?.id)}
                  formatter={(value) => String(parseInt((String(value || 0)), 10))}
                  parser={(value) => parseInt(value, 10)}
               />
            )
         },
         width: 120,
         align: 'center',
      },
      {
         key: 'isMarketPlace',
         dataIndex: 'isMarketPlace',
         title: 'Market place',
         render: (d: boolean, record) => {            
            return (
               <Checkbox onChange={(e) => handleInputNumberChange('isMarketPlace',e.target.checked, record?.id)} defaultChecked={d} />
            )
         },
         width: 160,
         align: 'center',
      },
      {
         key: 'isWebsite',
         dataIndex: 'isWebsite',
         title: 'Website',
         render: (d: boolean, record) => {
            return (
               <Checkbox onChange={(e) => handleInputNumberChange('isWebsite',e.target.checked, record?.id)} defaultChecked={d} />
            )
         },
         width: 120,
         align: 'center',
      },
      {
         key: 'id',
         dataIndex: 'id',
         title: 'Action',
         render: (id) => (
            <Button
               className="btn-primary-light"
               onClick={() => handleRemoveRelatedLocation(id)}
               loading={id === itemRemoveCurr ? true : false}
            >
               {t('brands.remove')}
            </Button>
         ),
         width: 120,
         fixed: 'right',
         align: 'center',
       },
   ];

   useEffect(() => {
      if (isOrderProcess) {
         form.setFieldValue('locationRelated', dataSource);
      }
      if (isCreate && !isOrderProcess) {
         setDataSource([]);
         form.setFieldValue('locationRelated', []);
      }
   }, [dataSource, isCreate, isOrderProcess]);

   useEffect(() => {
      if(Array.isArray(locationsCurr?.locationRelated) && locationsCurr?.locationRelated?.length > 0) {
         setDataSource(locationsCurr?.locationRelated);
      }
   }, [locationsCurr]);

   return (
      <Card
         title={t('mores.locations.relatedLocation')}
         extra={(
            <div
               className="text-highlight text-sm pointer"
               onClick={() => relatedLocationModalRef.current.open(true)}
            >
               {t('mores.locations.addRelatedLocation')}
            </div>
         )}
      >
         <FormItem
            name="locationRelated"
            hidden
         />
         <Table
            columns={columns}
            dataSource={mapRelatedLocation(dataSource)}
            pagination={false}
            scroll={{ x: 'max-content' }}
         />
         <SearchProductModal
            model="locations"
            ref={relatedLocationModalRef}
            columns={columnsModal}
            onAdd={handleAddRelatedLocation}
            loading={loading}
            initialFilter={initialFilterModal}
            searchKeys={['name']}
            customTitle={t('mores.locations.addLocation')}
            placeholderInputSearch={t('mores.locations.searchByLocation')}
         />
      </Card>
   )
}